import {
  EnrichedTrackerSchema,
  TrackerMessageSchema,
  TrackerMessageTemplateBasedPrototypeSchema,
} from '@racemap/sdk/schema/trackers';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { produce } from 'immer';
import { Tristate } from 'src/components/utils/tristateTask';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { revalidateTrackerMessages } from '../../../lib/customHooks/useTrackerMessagesPaged';
import type { DraftState, State } from '../../reducers';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createMessageActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  | 'addMessageToTracker'
  | 'removeTrackerMessage'
  | 'revokeTrackerMessage'
  | 'revokeAllTrackerMessages'
  | 'clearTrackerMessage'
  | 'clearAllTrackerMessages'
  | 'updateTrackerMessage'
> => ({
  addMessageToTracker: async ({
    trackerIds,
    plannedAt,
    closeAfter,
    transport,
    templateId,
    parameters,
  }) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      const template = get().trackers.messageTemplates.get(templateId.toHexString());
      if (template == null) throw new Error('Template not found');

      const message = TrackerMessageTemplateBasedPrototypeSchema.parse({
        label: template.name,
        closeAfter,
        plannedAt,
        transport,
        templateId,
        parameters,
      });
      const { success, messages, errors } = await apiClient.addTrackerMessages({
        ...message,
        trackerIds,
      });

      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      const affectedTrackerIds = [...new Set(messages.map((m) => m.trackerId.toHexString()))];

      if (!success || errors.length > 0) {
        const lines = errors.map(
          (e) => `Template ${e.templateId}, Tracker ${e.trackerId}: ${e.message}`,
        );
        toast.error(`Failed to send ${lines.length} message(s):\n${lines.join('\n')}`, {
          autoClose: false,
        });
      } else {
        toast.success(`${messages.length} message(s) sent.`);
      }
      void revalidateTrackerMessages(affectedTrackerIds).catch(console.error);
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof Error) {
        console.error(`Failed to add message to tracker: ${error.message}`);
        toast.error('The send message to tracker.');
      }
    }
  },
  removeTrackerMessage: async (trackerId, messageId) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      await apiClient.removeTrackerMessage(messageId.toHexString());
      const updated = await apiClient.getTracker(trackerId.toHexString());

      set(
        produce((s: DraftState) => {
          s.trackers.items.set(trackerId.toHexString(), EnrichedTrackerSchema.parse(updated));
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      void revalidateTrackerMessages([trackerId]).catch(console.error);
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof Error) {
        console.log(`Failed to remove message to tracker: ${error.message}`);
        toast.error('Failed to remove message.');
      }
    }
  },
  revokeTrackerMessage: async (trackerId, messageId) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      await apiClient.revokeTrackerMessage(messageId.toHexString());
      const updated = await apiClient.getTracker(trackerId.toHexString());

      set(
        produce((s: DraftState) => {
          s.trackers.items.set(trackerId.toHexString(), EnrichedTrackerSchema.parse(updated));
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      void revalidateTrackerMessages([trackerId]).catch(console.error);
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof HTTPFetchError) {
        console.log(error.serverError);
        toast.error(`Failed: ${error.serverError}`);
      } else if (error instanceof Error) {
        console.log(`Failed to revoke message to tracker: ${error.message}`);
        toast.error('Failed to revoke message.');
      }
    }
  },
  revokeAllTrackerMessages: async (trackerIds) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      const trackerIdsToRevoke = trackerIds.map((t) => t.toHexString());
      await apiClient.revokeAllTrackerMessages(trackerIdsToRevoke);
      const updatedTrackers = await Promise.all(
        trackerIdsToRevoke.map((tid) => apiClient.getTracker(tid)),
      );

      set(
        produce((s: DraftState) => {
          for (const updated of updatedTrackers) {
            s.trackers.items.set(updated.id.toHexString(), EnrichedTrackerSchema.parse(updated));
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      void revalidateTrackerMessages(trackerIdsToRevoke).catch(console.error);
      return true;
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof HTTPFetchError) {
        console.log(error.serverError);
        toast.error(`Failed: ${error.serverError}`);
      } else if (error instanceof Error) {
        console.log(`Failed to revoke all pending messages to tracker: ${error.message}`);
        toast.error('Failed to revoke all pending messages.');
      }
      return false;
    }
  },
  clearTrackerMessage: async (trackerId, messageId) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      await apiClient.clearTrackerMessage(messageId.toHexString());
      const updated = await apiClient.getTracker(trackerId.toHexString());

      set(
        produce((s: DraftState) => {
          s.trackers.items.set(trackerId.toHexString(), EnrichedTrackerSchema.parse(updated));
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      void revalidateTrackerMessages([trackerId]).catch(console.error);
      return true;
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof HTTPFetchError) {
        console.log(error.serverError);
        toast.error(`Failed: ${error.serverError}`);
      } else if (error instanceof Error) {
        console.log(`Failed to clear message to tracker: ${error.message}`);
        toast.error('Failed to clear message.');
      }
      return false;
    }
  },
  clearAllTrackerMessages: async (trackerIds) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      const trackerIdsToClear = trackerIds.map((t) => t.toHexString());
      await apiClient.clearAllTrackerMessages(trackerIdsToClear);
      const updatedTrackers = await Promise.all(
        trackerIdsToClear.map((tid) => apiClient.getTracker(tid)),
      );

      set(
        produce((s: DraftState) => {
          for (const updated of updatedTrackers) {
            s.trackers.items.set(updated.id.toHexString(), EnrichedTrackerSchema.parse(updated));
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      void revalidateTrackerMessages(trackerIdsToClear).catch(console.error);
      return true;
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof HTTPFetchError) {
        console.log(error.serverError);
        toast.error(`Failed: ${error.serverError}`);
      } else if (error instanceof Error) {
        console.log(`Failed to clear all error messages to tracker: ${error.message}`);
        toast.error('Failed to clear all error messages.');
      }
      return false;
    }
  },
  updateTrackerMessage: async (messageId, newTrackerMessage) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      const update = TrackerMessageSchema.parse(
        await apiClient.updateTrackerMessage(messageId.toHexString(), newTrackerMessage),
      );
      const trackerId = update.trackerId;
      const updated = await apiClient.getTracker(trackerId.toHexString());

      set(
        produce((s: DraftState) => {
          s.trackers.items.set(trackerId.toHexString(), EnrichedTrackerSchema.parse(updated));
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      void revalidateTrackerMessages([trackerId]).catch(console.error);
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof HTTPFetchError) {
        console.log(error.serverError);
        toast.error(`Failed: ${error.serverError}`);
      } else if (error instanceof Error) {
        console.log(`Failed to update message to tracker: ${error.message}`);
        toast.error('Failed to update message.');
      }
    }
  },
});
